/**
 * /me/attendance — employee self-service clock in/out + my recent punches
 * (ADR-0014 Phase 4 PR-2). Mirrors myLeave's page/section/table conventions.
 *
 * Geofence (D-8) is flag-not-block. The out-of-range flag stays a server-side
 * signal for HR; but for transparency (Phase 6 / G-4) the employee IS shown the
 * NAME of the office they're assigned to clock from (or that they aren't
 * geofenced) — they just never see coordinates or their own flag.
 */
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { meLayoutRoute } from './meLayout.js';
import { getMyWorkLocation, listMyClockEvents, selfClock, type ClockDirection } from '../api/attendance.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { formatDate } from '../lib/format.js';

export const myAttendanceRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: '/me/attendance',
  component: MyAttendancePage,
});

function MyAttendancePage(): JSX.Element {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [lastAction, setLastAction] = useState<string | null>(null);

  const eventsQuery = useQuery({
    queryKey: ['me', 'clock-events'],
    queryFn: listMyClockEvents,
  });

  // The office the employee is assigned to clock from (transparency). null
  // location ⇒ not geofenced. We never show coordinates or the out-of-range flag.
  const locationQuery = useQuery({
    queryKey: ['me', 'work-location'],
    queryFn: getMyWorkLocation,
  });

  const clockMut = useMutation({
    // Capture GPS (Phase 6) so the server can confirm you're at your work site.
    // Denial/unavailability is fine — the punch still records, just un-evaluated.
    mutationFn: async (direction: ClockDirection) => selfClock({ direction, ...(await getCoords()) }),
    onSuccess: async (ev) => {
      setError(null);
      setLastAction(`Clocked ${ev.direction === 'clock_in' ? 'in' : 'out'} at ${formatTime(ev.occurredAt)}.`);
      await queryClient.invalidateQueries({ queryKey: ['me', 'clock-events'] });
    },
    onError: (err: unknown) => {
      setLastAction(null);
      setError(err instanceof ApiError ? err.message : 'Could not record your punch.');
    },
  });

  const events = eventsQuery.data?.events ?? [];

  return (
    <section className="p-8 space-y-6 max-w-3xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">My attendance</h1>
        <p className="text-sm text-gray-400">Clock in when you start and out when you finish. Your recent punches are below.</p>
      </header>

      <div className="rounded border border-gray-900 p-4 space-y-3">
        {lastAction ? (
          <div role="status" className="rounded border border-green-900 bg-green-950/40 px-3 py-2 text-sm text-green-300">
            {lastAction}
          </div>
        ) : null}
        {error ? <Alert variant="error">{error}</Alert> : null}
        {locationQuery.data ? (
          locationQuery.data.location ? (
            <p className="text-sm text-gray-300">
              <span className="text-gray-500">Your work location:</span>{' '}
              <span className="font-medium">{locationQuery.data.location.name}</span>{' '}
              <span className="text-gray-500">— clock in within {locationQuery.data.location.radiusMeters} m of the site.</span>
            </p>
          ) : (
            <p className="text-sm text-gray-400">You&rsquo;re not assigned to a specific office — you can clock in from anywhere.</p>
          )
        ) : null}
        <p className="text-xs text-gray-500">
          Your location is requested when you clock, to confirm you&rsquo;re at your work site. Declining is fine — your punch is still recorded.
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => clockMut.mutate('clock_in')}
            disabled={clockMut.isPending}
            className="rounded bg-green-700 text-white text-sm font-medium px-4 py-2 disabled:opacity-50"
          >
            {clockMut.isPending ? 'Recording…' : 'Clock in'}
          </button>
          <button
            type="button"
            onClick={() => clockMut.mutate('clock_out')}
            disabled={clockMut.isPending}
            className="rounded border border-gray-700 text-sm px-4 py-2 hover:bg-gray-900 disabled:opacity-50"
          >
            Clock out
          </button>
        </div>
      </div>

      <RecentPunches events={events} loading={eventsQuery.isLoading} error={eventsQuery.isError} />
    </section>
  );
}

function RecentPunches({
  events,
  loading,
  error,
}: {
  events: Array<{ id: string; occurredAt: string; direction: ClockDirection; source: string }>;
  loading: boolean;
  error: boolean;
}): JSX.Element {
  if (loading) {
    return <div className="rounded border border-gray-900 p-6 text-center text-gray-500 text-sm">Loading…</div>;
  }
  if (error) {
    return <Alert variant="error">Could not load your punches.</Alert>;
  }
  if (events.length === 0) {
    return <section className="rounded border border-gray-900 p-6 text-center text-sm text-gray-500">No punches yet.</section>;
  }
  return (
    <div className="rounded border border-gray-900 overflow-hidden">
      <header className="px-4 py-3 text-xs uppercase tracking-wide text-gray-400 border-b border-gray-900">Recent punches</header>
      <table className="w-full text-sm">
        <thead className="bg-gray-950 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="text-left px-4 py-2">When</th>
            <th className="text-left px-4 py-2">Direction</th>
            <th className="text-left px-4 py-2">Source</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.id} className="border-t border-gray-900">
              <td className="px-4 py-3 font-mono text-xs text-gray-300">{formatDate(e.occurredAt)} {formatTime(e.occurredAt)}</td>
              <td className="px-4 py-3 text-gray-300">{e.direction === 'clock_in' ? 'Clock in' : 'Clock out'}</td>
              <td className="px-4 py-3 text-gray-500">{e.source}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatTime(iso: string): string {
  // HH:MM in UTC — matches the rest of the app's UTC date handling.
  return new Date(iso).toISOString().slice(11, 16);
}

/**
 * Best-effort browser geolocation (Phase 6). Resolves to {} on denial, timeout,
 * or no support — the punch records without coordinates (flag-not-block); the
 * server never blocks and never flags an un-located punch.
 */
function getCoords(): Promise<{ latitude?: number; longitude?: number }> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return resolve({});
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve({}),
      { timeout: 8000, maximumAge: 60_000 },
    );
  });
}
